import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import StarIcon from "@mui/icons-material/Star";
import { Box, Stack } from "@mui/system";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { customerYotoService } from "~/connectors/yoto.connector";
import { formatDate, formatDuration } from "~/lib/formatDate";
import { YotoCard } from "~/types";
import { getYotoIconUrlFromMediaId } from "./getYotoMediaIdUrl";

import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import CardActionArea from "@mui/material/CardActionArea";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import ListItemButton from "@mui/material/ListItemButton";

export default function YotoCardItem({
  card,
  isOurCard,
  defaultExpanded = false,
}: {
  card: YotoCard;
  isOurCard: boolean;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [fullCard, setFullCard] = useState<YotoCard | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [contentError, setContentError] = useState(false);
  const router = useRouter();

  const coverUrl = card.metadata?.cover?.imageL ?? card.metadata?.cover?.imageM;
  const tracks =
    (fullCard ?? card).content?.chapters?.flatMap((ch) => ch.tracks ?? []) ??
    [];

  const fetchCardContent = useCallback(async () => {
    setLoadingContent(true);
    setContentError(false);
    try {
      const { card: fetched } = await customerYotoService.getCard(card.cardId);
      setFullCard(fetched);
    } catch {
      setContentError(true);
    } finally {
      setLoadingContent(false);
    }
  }, [card.cardId]);

  useEffect(() => {
    if (defaultExpanded) {
      fetchCardContent();
    }
  }, [defaultExpanded, fetchCardContent]);

  const handleToggle = async () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (newExpanded && !fullCard) {
      await fetchCardContent();
    }
  };

  const coverImage = coverUrl ? (
    <Box
      component="img"
      src={coverUrl}
      alt={card.title}
      sx={{
        width: 56,
        height: 56,
        borderRadius: 1,
        objectFit: "cover",
        flexShrink: 0,
      }}
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = "none";
      }}
    />
  ) : (
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: 1,
        bgcolor: "yoto.light",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <MusicNoteIcon sx={{ color: "yoto.contrastText", fontSize: 28 }} />
    </Box>
  );

  return (
    <Card variant="outlined">
      <CardActionArea onClick={handleToggle} sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={2} sx={{
          alignItems: "center"
        }}>
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            {coverImage}
            {isOurCard && (
              <Box
                sx={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  bgcolor: "secondary.main",
                  borderRadius: "50%",
                  width: 18,
                  height: 18,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <StarIcon
                  sx={{ fontSize: 11, color: "secondary.contrastText" }}
                />
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{
              fontWeight: 600
            }}>
              {card.title}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                flexWrap: "wrap",
                mt: 0.5
              }}>
              {tracks.length > 0 && (
                <Chip
                  label={`${tracks.length} ${tracks.length === 1 ? "melodie" : "melodii"}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {card.createdAt && (
                <Typography variant="caption" sx={{
                  color: "text.secondary"
                }}>
                  Creat: {formatDate(card.createdAt)}
                </Typography>
              )}
              {card.updatedAt &&
                card.createdAt &&
                formatDate(card.updatedAt) !== formatDate(card.createdAt) && (
                  <Typography variant="caption" sx={{
                    color: "text.secondary"
                  }}>
                    Actualizat: {formatDate(card.updatedAt)}
                  </Typography>
                )}
            </Stack>
          </Box>
          {expanded ? (
            <ExpandLessIcon color="action" />
          ) : (
            <ExpandMoreIcon color="action" />
          )}
        </Stack>
      </CardActionArea>
      <Collapse in={expanded}>
        <Divider />
        {loadingContent ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : contentError ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="error">
              Nu s-au putut încărca detaliile cardului. Încearcă din nou.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {tracks.map((track, idx) => {
              const trackContent = (
                <>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {track.display?.icon16x16 ? (
                      <Box
                        component="img"
                        src={getYotoIconUrlFromMediaId(track.display.icon16x16)}
                        alt=""
                        sx={{
                          width: 16,
                          height: 16,
                          imageRendering: "pixelated",
                        }}
                      />
                    ) : (
                      <MusicNoteIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                    )}
                  </ListItemIcon>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minWidth: 0,
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          width: 14,
                          textAlign: "center",
                          flexShrink: 0,
                          mr: 0.25,
                        }}
                      >
                        {track.overlayLabel}.
                      </Typography>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {track.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        flexShrink: 0,
                        ml: 2
                      }}>
                      {track.duration ? formatDuration(track.duration) : "0s"}
                    </Typography>
                  </Box>
                </>
              );

              return isOurCard ? (
                <ListItemButton
                  key={idx}
                  sx={{ py: 0.5, px: 2 }}
                  onClick={() => router.push("/yoto/cantece")}
                >
                  {trackContent}
                </ListItemButton>
              ) : (
                <ListItem key={idx} sx={{ py: 0.5, px: 2 }}>
                  {trackContent}
                </ListItem>
              );
            })}
            {tracks.length === 0 && (
              <ListItem sx={{ py: 1, px: 2 }}>
                <ListItemText
                  primary="Nicio melodie pe acest card"
                  slotProps={{
                    primary: { variant: "body2", color: "text.secondary" },
                  }}
                />
              </ListItem>
            )}
          </List>
        )}
      </Collapse>
    </Card>
  );
}
